import { memo, useMemo, type CSSProperties } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useGetEventUsers } from "../../api/queries/useGetEventUsers";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { BookedEvents, Event } from "../../types/Types";
import {
  getEventCardPhase,
  isWithin48HoursBeforeEvent,
  parseEventDateAsLocalDay,
  type EventCardPhase,
} from "../../utils/eventDateUtils";
import { isEventEarlyBirdConfigured } from "../../utils/eventEarlyBirdUtils";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import { ClampedTextWithTooltip } from "./ClampedTextWithTooltip";
import {
  ATTENDEE_AVATAR_PREVIEW_COUNT,
  ATTENDEE_AVATAR_SIZE_PX,
  EVENT_CARD_IMAGES,
  META_CLASS,
  META_STYLE,
  PHASE_CARD_CLASS,
  SLOT,
  type EventsListMode,
} from "./eventsListShared";

function getAttendeeAvatarPreviews(users: unknown[] | undefined) {
  if (!users?.length) return [];
  return users.slice(0, ATTENDEE_AVATAR_PREVIEW_COUNT).map((raw, index) => {
    const row = (raw ?? {}) as Record<string, unknown>;
    const nested =
      row.user ?? row.User ?? row.applicationUser ?? row.ApplicationUser;
    const src =
      nested && typeof nested === "object" && !Array.isArray(nested)
        ? { ...row, ...(nested as Record<string, unknown>) }
        : row;
    const first = String(src.firstName ?? src.FirstName ?? "").trim();
    const last = String(src.lastName ?? src.LastName ?? "").trim();
    const photo = String(
      src.professionalPhoto ??
        src.ProfessionalPhoto ??
        src.personalProfessionalPhoto ??
        src.PersonalProfessionalPhoto ??
        "",
    ).trim();
    return {
      key: String(
        src.objectId ?? src.ObjectId ?? src.email ?? src.Email ?? index,
      ),
      photo,
      initials: `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?",
    };
  });
}

const EventAttendeeAvatarStack = memo(function EventAttendeeAvatarStack({
  eventId,
  enabled,
}: {
  eventId: number;
  enabled: boolean;
}) {
  const { data } = useGetEventUsers({ id: String(eventId), enabled });
  const previews = useMemo(
    () => getAttendeeAvatarPreviews(data?.users as unknown[] | undefined),
    [data?.users],
  );

  if (!enabled || !previews.length) {
    return <i className="bi bi-people me-1" aria-hidden />;
  }

  return (
    <span className="d-inline-flex align-items-center flex-shrink-0" aria-hidden>
      {previews.map((p, i) => (
        <span
          key={p.key}
          className="rounded-circle border border-2 border-white bg-secondary text-white d-inline-flex align-items-center justify-content-center overflow-hidden flex-shrink-0"
          style={{
            width: ATTENDEE_AVATAR_SIZE_PX,
            height: ATTENDEE_AVATAR_SIZE_PX,
            fontSize: "0.55rem",
            marginLeft: i === 0 ? 0 : -7,
            zIndex: previews.length - i,
            position: "relative",
          }}
        >
          {p.photo ? (
            <img src={p.photo} alt="" className="w-100 h-100 object-fit-cover" />
          ) : (
            p.initials
          )}
        </span>
      ))}
    </span>
  );
});

export const EventListCard = memo(function EventListCard({
  event,
  listMode,
  isBooked,
  isFavorite,
  onToggleFavorite,
  onBook,
  onViewBooked,
  onViewAttendees,
  getBookedEvent,
}: {
  event: Event;
  listMode: EventsListMode;
  isBooked: boolean;
  isFavorite: boolean;
  onToggleFavorite: (event: Event) => void;
  onBook: (eventId: number) => void;
  onViewBooked: (event: BookedEvents) => void;
  onViewAttendees: (eventId: number) => void;
  getBookedEvent: (eventId: number) => BookedEvents | undefined;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage || i18n.language;
  const eventDate = parseEventDateAsLocalDay(event.eventDate);
  const phase: EventCardPhase =
    listMode === "attended" ? "expired" : getEventCardPhase(event.eventDate);
  const hasEarlyBird = isEventEarlyBirdConfigured(event);
  const isEarlyBirdActive =
    hasEarlyBird &&
    !!event.earlyBirdDateEnd &&
    new Date(event.earlyBirdDateEnd) > new Date();
  const showAttendees =
    isBooked ||
    listMode === "attended" ||
    isWithin48HoursBeforeEvent(event.eventDate);

  const dynamicFields = useMemo(
    () => ({
      name: event.name ?? "",
      description: event.description ?? "",
      location: event.location ?? "",
    }),
    [event.name, event.description, event.location],
  );
  const { data: translatedFields } = useTranslateDynamicFields(
    dynamicFields,
    dateLocale,
  );
  const tx = (key: keyof typeof dynamicFields, fallback: string) =>
    translatedFields?.[key]?.trim() || fallback;

  const phaseLabel =
    phase === "today"
      ? t("events.today")
      : phase === "upcoming"
        ? t("events.upcoming")
        : t("events.ended");
  const ribbonLabel = isBooked ? t("events.bookedBanner") : phaseLabel;
  const ribbonIcon = isBooked
    ? "bi-check-circle-fill"
    : phase === "today"
      ? "bi-calendar-event-fill"
      : phase === "upcoming"
        ? "bi-clock-fill"
        : "bi-x-circle-fill";
  const ribbonTone = isBooked
    ? "text-white"
    : phase === "today"
      ? "bg-success text-white"
      : phase === "upcoming"
        ? "bg-project-gradient text-white"
        : "bg-danger text-white";
  const ribbonStyle: CSSProperties = {
    width: 120,
    top: 18,
    right: -28,
    transform: "rotate(45deg)",
    padding: "0.2rem 0",
    fontSize: "0.65rem",
    letterSpacing: "0.03em",
    ...(isBooked ? { backgroundColor: "#6f42c1" } : {}),
  };
  const title = tx("name", event.name);
  const description = tx("description", event.description ?? "");
  const locationLabel = tx("location", event.location ?? "") || t("common.na");
  const currency = event.currency ?? "gbp";
  const imageUrl =
    event.bannerImage || EVENT_CARD_IMAGES[event.id % EVENT_CARD_IMAGES.length];

  return (
    <article className={`${PHASE_CARD_CLASS[phase]} h-100 d-flex flex-column`}>
      <div className="p-2 pb-0">
        <div className="position-relative rounded-3 overflow-hidden">
          <div className="ratio ratio-16x9">
            <img
              src={imageUrl}
              alt={title}
              className="object-fit-cover w-100 h-100"
            />
          </div>
          <span className="position-absolute top-0 start-0 m-2 badge bg-white text-dark border border-white border-2 px-2 py-1 fw-normal small">
            <i className="bi bi-calendar3 me-1" aria-hidden />
            {eventDate.toLocaleDateString(dateLocale, {
              day: "numeric",
              month: "short",
              year: "numeric",
            }) || t("common.na")}
          </span>
          <div
            className="position-absolute top-0 end-0 overflow-hidden"
            style={{ width: 88, height: 88, pointerEvents: "none" }}
            aria-label={ribbonLabel}
          >
            <span
              className={`position-absolute ${ribbonTone} small fw-semibold text-uppercase d-inline-flex align-items-center justify-content-center gap-1 shadow-sm`}
              style={ribbonStyle}
            >
              <i className={`bi ${ribbonIcon}`} aria-hidden />
              {ribbonLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="card-body px-3 py-2 d-flex flex-column small flex-grow-1">
        <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
          <button
            type="button"
            className="btn btn-link p-0 text-dark flex-shrink-0 lh-1"
            aria-label={t("events.favorite")}
            aria-pressed={isFavorite}
            onClick={() => onToggleFavorite(event)}
          >
            <i
              className={`bi ${isFavorite ? "bi-bookmark-fill" : "bi-bookmark"}`}
              style={{ fontSize: "1.15rem" }}
              aria-hidden
            />
          </button>
        </div>

        <div className="mb-2" style={SLOT.title}>
          <ClampedTextWithTooltip
            id={`event-title-${event.id}`}
            text={title}
            className="fw-bold text-dark mb-0"
            lines={1}
          />
        </div>
        <div className="mb-2" style={SLOT.desc}>
          {description ? (
            <ClampedTextWithTooltip
              id={`event-desc-${event.id}`}
              text={description}
              className={META_CLASS}
              style={META_STYLE}
            />
          ) : null}
        </div>
        <div
          className={`${META_CLASS} mb-0 d-flex align-items-center min-w-0`}
          style={SLOT.location}
        >
          <i className="bi bi-geo-alt me-1 flex-shrink-0" aria-hidden />
          <ClampedTextWithTooltip
            id={`event-location-${event.id}`}
            text={locationLabel}
            className={`${META_CLASS} min-w-0 flex-grow-1`}
            style={META_STYLE}
            lines={1}
          />
        </div>

        <div className="mt-auto w-100 pt-2">
          <hr className="my-1" />
          <div
            className={`d-flex align-items-start gap-2 mb-2 ${
              showAttendees ? "justify-content-between" : "justify-content-end"
            }`}
            style={SLOT.pricing}
          >
            {showAttendees && (
              <button
                type="button"
                className="btn btn-link p-0 link-underline link-underline-opacity-0 link-underline-opacity-100-hover d-inline-flex align-items-center gap-1 min-w-0 flex-fill"
                style={
                  {
                    fontSize: "12px",
                    lineHeight: 1.2,
                    "--bs-btn-color": "var(--bs-dark)",
                    "--bs-btn-hover-color": "var(--bs-primary)",
                  } as CSSProperties
                }
                onClick={() => onViewAttendees(event.id)}
              >
                <EventAttendeeAvatarStack
                  eventId={event.id}
                  enabled={showAttendees}
                />
                <span className="text-truncate">{t("events.viewAttendees")}</span>
              </button>
            )}
            <div
              className={`text-end min-w-0 ${showAttendees ? "flex-fill" : ""}`}
              style={{ fontSize: "12px", lineHeight: 1.2 }}
            >
              <p className="fw-bold text-primary mb-0 text-break">
                {t("events.price")}: {formatCurrencyAmount(event.price, currency)}
              </p>
              {hasEarlyBird && (
                <>
                  <p
                    className={`text-muted mb-0 text-break ${
                      !isEarlyBirdActive ? "text-decoration-line-through" : ""
                    }`}
                    style={{ fontSize: "11px" }}
                  >
                    {t("events.earlyBirdDiscount")}:{" "}
                    {formatCurrencyAmount(event.earlyBirdPrice, currency)}
                  </p>
                  {isEarlyBirdActive && (
                    <p className="mb-0">
                      <span className="text-success">
                        {t("events.earlyBirdEndsOn")}:{" "}
                        {new Date(event.earlyBirdDateEnd!).toLocaleDateString(
                          dateLocale,
                        )}
                      </span>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {listMode === "attended" || isBooked ? (
            <Button
              size="sm"
              className="w-100 bg-project-gradient border-0 text-white"
              type="button"
              onClick={() => {
                const booked = getBookedEvent(event.id);
                if (booked) onViewBooked(booked);
              }}
            >
              {t("events.viewBookedEvent")}
            </Button>
          ) : event.seats > 0 ? (
            <Button
              variant="primary"
              size="sm"
              className="w-100 text-white"
              type="button"
              onClick={() => onBook(event.id)}
            >
              {t("events.bookNow")}
            </Button>
          ) : (
            <Button
              variant="danger"
              size="sm"
              className="w-100 text-white"
              disabled
              type="button"
            >
              {t("events.soldOut")}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
});
